import axios from 'axios'
import TransactionSchema, {
  AdminTransactionsSchema,
  AdminTransactionsSchemaType,
  PaginatedTransactions,
} from '@/queries/transactions/transactions.types.ts'
import { parseBlobRequestError } from '@/common/utilities.ts'
import { toUtcEndOfDayISOString, toUtcStartOfDayISOString } from '@/lib/utils'
import { DateRangeParams } from '@/common/types_and_interfaces'

const TransactionsQueryKeys = {
  list_all: ['transactions'],
  paginateTransactions: (
    program_id: number,
    recordsPerPage: number,
    currentPage: number,
    global_on_id: number,
    locale = 'en'
  ) => [
    ...TransactionsQueryKeys.list_all,
    program_id,
    recordsPerPage,
    currentPage,
    global_on_id,
    locale,
  ],
} as const

export type TransactionSortBy =
  | 'transaction_id'
  | 'applicant_name'
  | 'student_name'
  | 'transaction_type'
  | 'provider_name'
  | 'invoice_quote'
  | 'total_amount'
  | 'uuid'
  | 'status'
  | 'created_datetime'

export const getPaginatedTransactions = async (
  program_id: number,
  currentPage: number,
  recordsPerPage: number,
  global_on_id: number,
  locale: string,
  search: string,
  sort_by?: TransactionSortBy,
  sort_order?: 'asc' | 'desc',
  dateRange?: DateRangeParams
): Promise<PaginatedTransactions> => {
  const response = await axios.get('/transactions', {
    params: {
      program_id,
      paginate: 1,
      per_page: recordsPerPage,
      page: currentPage,
      global_on_id,
      i18n: locale,
      search,
      ...(sort_by && { sort_by }),
      ...(sort_order && { sort_order }),
      ...(dateRange?.startDate && {
        start_datetime: toUtcStartOfDayISOString(dateRange.startDate),
        end_datetime: toUtcEndOfDayISOString(
          dateRange.endDate ?? dateRange.startDate
        ),
      }),
    },
  })
  return response.data
}

export const getTransactionById = async (
  transaction_id: number,
  program_id: number
) => {
  const response = await axios.get(`/transactions/${transaction_id}`, {
    params: {
      program_id,
    },
  })
  return TransactionSchema.parse(response.data)
}

type Identifier = { provider_id: number } | { applicant_id: number }

export const getTransactionsAdmin = async (
  program_id: number,
  currentPage: number,
  recordsPerPage: number,
  search: string,
  global_on_id: number,
  locale: string,
  identifier: Identifier
): Promise<AdminTransactionsSchemaType> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const params: Record<string, any> = {
    program_id,
    paginate: 1,
    per_page: recordsPerPage,
    page: currentPage,
    global_on_id,
    i18n: locale,
    ...(search && { search, search_fields: 'basic' }),
    ...identifier,
  }
  const response = await axios.get('/transactions', { params })
  return AdminTransactionsSchema.parse(response.data)
}

export type ExportTransactionsParams = {
  programId: number
  search?: string
} & DateRangeParams

const buildExportTransactionsQueryParams = ({
  programId,
  search,
  startDate,
  endDate,
}: ExportTransactionsParams) => ({
  program_id: programId,
  ...(search && { search }),
  ...(startDate && {
    start_datetime: toUtcStartOfDayISOString(startDate),
    end_datetime: toUtcEndOfDayISOString(endDate ?? startDate),
  }),
})

export const exportTransactions = async (
  params: ExportTransactionsParams
): Promise<Blob> => {
  try {
    const response = await axios.get('/transactions/export', {
      responseType: 'blob',
      params: buildExportTransactionsQueryParams(params),
    })

    return response.data
  } catch (error) {
    throw new Error((await parseBlobRequestError(error)).message)
  }
}

export const exportSpendReport = async (
  params: ExportTransactionsParams
): Promise<Blob> => {
  try {
    const response = await axios.get('/transactions/spend_report/export', {
      responseType: 'blob',
      params: buildExportTransactionsQueryParams(params),
    })

    return response.data
  } catch (error) {
    throw new Error((await parseBlobRequestError(error)).message)
  }
}
